import { useCallback, useEffect, useState } from "react";
import {
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { cx } from "../utils/cx";
import { Card, Container, EmptyState, Loading, PageHeader, Tabs } from "./ui";
import { bursariesApi } from "../api/hliLists";
import { problemOf } from "../api/scholarships";
import ClaimRecordCard from "./studentLoan/ClaimRecordCard";
import LoanStatusTab from "./studentLoan/LoanStatusTab";
import ApplyTab from "./studentLoan/ApplyTab";
import ResultClaimTab from "./studentLoan/ResultClaimTab";
import ContactTab from "./studentLoan/ContactTab";

const ICON = "h-4 w-4";
const TABS = [
  { value: "status", label: "Loan status", icon: <ClipboardDocumentCheckIcon className={ICON} /> },
  { value: "apply", label: "Apply for local loan", icon: <DocumentTextIcon className={ICON} /> },
  { value: "claim", label: "Submit claim", icon: <ChatBubbleLeftEllipsisIcon className={ICON} /> },
  { value: "contact", label: "Update national ID and phone", icon: <PhoneIcon className={ICON} /> },
];

/* The signed-in student claims their bursary record (their institution uploads its admitted students),
   then applies for a local loan, checks its status, submits claims and updates their contact details
   against that record only. Their institution's HLI admin, or the HEC Admin, grants or rejects it. */
export default function StudentLocalLoan() {
  // { record, requestsOpen, request }, as the Loans service answers
  const [bursary, setBursary] = useState(null);
  const [recordState, setRecordState] = useState("loading");
  const [tab, setTab] = useState("status");

  const show = (data) => {
    setBursary(data);
    setRecordState("claimed");
  };

  const loadRecord = useCallback(async () => {
    setRecordState((state) => (state === "claimed" ? state : "loading"));
    try {
      show((await bursariesApi.mine()).data);
    } catch (error) {
      // The account is not linked to a bursary record yet
      if (problemOf(error)?.code === "not-claimed") {
        setRecordState("unclaimed");
      } else {
        console.error("Failed to load the bursary record", error);
        setRecordState("error");
      }
    }
  }, []);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  // Every tab stays mounted so typed input survives switching tabs
  const panels = bursary && {
    status: <LoanStatusTab bursary={bursary} />,
    apply: (
      <ApplyTab
        bursary={bursary}
        onApplied={(data) => {
          show(data);
          setTab("status");
        }}
      />
    ),
    claim: <ResultClaimTab record={bursary.record} />,
    contact: <ContactTab record={bursary.record} onUpdated={show} />,
  };

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Local Loan" subtitle="Apply for a local loan (undergraduate), which your institution grants or rejects, check your loan status and submit claims" />

      {recordState === "loading" && <Loading label="Loading your bursary record…" />}
      {recordState === "error" && (
        <EmptyState title="Could not load your bursary record" description="Please, try again later." onRefresh={loadRecord} />
      )}
      {recordState === "unclaimed" && <ClaimRecordCard onClaimed={show} />}
      {recordState === "claimed" && (
        <>
          <div className="thin-scrollbar overflow-x-auto">
            <Tabs tabs={TABS} value={tab} onChange={setTab} />
          </div>
          <Card>
            {TABS.map(({ value }) => (
              <div key={value} role="tabpanel" className={cx(tab !== value && "hidden")}>
                {panels[value]}
              </div>
            ))}
          </Card>
        </>
      )}
    </Container>
  );
}
